import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import io from 'socket.io-client';
import { IconComponents } from '../components/icons';

const DEFAULT_IMAGE = '/images/default-product.png';

const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category');
  const [searchTerm, setSearchTerm] = useState('');

  // Fonction pour obtenir les produits filtrés
  const getFilteredProducts = useCallback(() => {
    let filtered = selectedCategory
      ? products.filter(p => p.type === selectedCategory)
      : products;
    
    // Appliquer le filtre de recherche
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.title.toLowerCase().includes(searchLower) ||
        product.description.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [selectedCategory, products, searchTerm]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsResponse, categoriesResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/products/catalog`),
          fetch(`${process.env.REACT_APP_API_URL}/api/categories`)
        ]);

        if (!productsResponse.ok) throw new Error('Failed to fetch products');
        if (!categoriesResponse.ok) throw new Error('Failed to fetch categories');

        const productsData = await productsResponse.json();
        const categoriesData = await categoriesResponse.json();

        // Précharger les images en arrière-plan
        if (Array.isArray(productsData)) {
          productsData.forEach(product => {
            if (product.image) {
              const img = new Image();
              img.src = product.image;
            }
          });
        }

        setProducts(Array.isArray(productsData) ? productsData : []);
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      } catch (error) {
        console.error('Error loading data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const renderIcon = (iconName, color = 'text-[#22c55e]') => {
    const Icon = IconComponents[iconName];
    return Icon ? (
      <div className={`w-6 h-6 ${color}`}>
        <Icon />
      </div>
    ) : null;
  };

  const getCategoryTitle = () => {
    if (!selectedCategory) return 'All Products';
    const category = categories.find(c => c.id === selectedCategory);
    return category ? category.name : 'All Products';
  };

  const scrollToProducts = (e) => {
    e.preventDefault();
    const productsSection = document.getElementById('products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Obtenir les produits filtrés
  const filteredProducts = getFilteredProducts();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22c55e]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-left text-red-500">
          <p>Error loading products: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div>
            <h1 className="text-[#22c55e] text-xl font-medium mb-2">
              Welcome to OLYMP
            </h1>
            <div className="text-[44px] font-bold text-white leading-tight mb-5">
              Digital Subscriptions & Discord Services
            </div>
            <p className="text-lg leading-relaxed text-gray-300 mb-6 max-w-3xl">
              Elevate your Discord experience with OLYMP! We provide premium Discord services and digital upgrades at the best prices. From server boosts to account upgrades, enjoy our 100% secure and reliable solutions with instant support.
            </p>
            <div className="mb-10">
              <button
                onClick={scrollToProducts}
                className="inline-block rounded-md bg-[#22c55e] px-7 py-2.5 text-base font-semibold text-black shadow-sm hover:bg-[#15803d] transition-all duration-300"
              >
                Browse products
              </button>
            </div>

            {/* Stats Section */}
            <div className="flex space-x-14">
              <div>
                <div className="text-[#22c55e] text-3xl font-bold">22+</div>
                <div className="text-gray-500 text-sm mt-1">Products</div>
              </div>
              <div>
                <div className="text-[#22c55e] text-3xl font-bold">5.0 / 5.0</div>
                <div className="text-gray-500 text-sm mt-1">Reputation</div>
              </div>
              <div>
                <div className="text-[#22c55e] text-3xl font-bold">2000+</div>
                <div className="text-gray-500 text-sm mt-1">Customers</div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Cards Section */}
        <div className="mx-auto max-w-7xl px-6 mt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {/* Safe and Legal Card */}
            <div className="group relative overflow-hidden rounded-lg bg-black/20 p-8 transition-all duration-500 hover:bg-[#22c55e]/5 border border-white/5 hover:border-[#22c55e]/20">
              <div className="relative z-10">
                <div className="mb-5 inline-flex rounded-lg bg-[#22c55e]/10 p-3">
                  <svg className="h-7 w-7 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white group-hover:text-[#22c55e] transition-colors duration-300">
                  Safe and Legal
                </h3>
                <p className="text-base text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                  Legally paid upgrades with regional pricing ensure security of your accounts. 0% kick rate and warranty offer peace of mind against stolen or hacked accounts.
                </p>
              </div>
            </div>

            {/* Quick Support Card */}
            <div className="group relative overflow-hidden rounded-lg bg-black/20 p-8 transition-all duration-500 hover:bg-[#22c55e]/5 border border-white/5 hover:border-[#22c55e]/20">
              <div className="relative z-10">
                <div className="mb-5 inline-flex rounded-lg bg-[#22c55e]/10 p-3">
                  <svg className="h-7 w-7 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white group-hover:text-[#22c55e] transition-colors duration-300">
                  Quick Support
                </h3>
                <p className="text-base text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                  Experience lightning-fast support! Our dedicated team is committed to providing quick and efficient responses to all your questions.
                </p>
              </div>
            </div>

            {/* Payment Flexibility Card */}
            <div className="group relative overflow-hidden rounded-lg bg-black/20 p-8 transition-all duration-500 hover:bg-[#22c55e]/5 border border-white/5 hover:border-[#22c55e]/20">
              <div className="relative z-10">
                <div className="mb-5 inline-flex rounded-lg bg-[#22c55e]/10 p-3">
                  <svg className="h-7 w-7 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white group-hover:text-[#22c55e] transition-colors duration-300">
                  Payment Flexibility
                </h3>
                <p className="text-base text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                  Choose the payment method that suits you best. We offer a variety of convenient options, including PayPal, Revolut, Bank transfer and Crypto.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Info Section */}
        <div className="mx-auto max-w-7xl px-6 mt-16">
          <div className="relative overflow-hidden rounded-lg bg-[#22c55e]/5 border border-[#22c55e]/20 p-6">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#22c55e] mb-2">Manual Delivery via Discord Support</h3>
                <p className="text-gray-300">
                  All products in this section are delivered manually by our support team through Discord tickets. This ensures personalized assistance and careful handling of your order, available 24/7. Join our Discord server to get started!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Navigation */}
      <div className="mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="md:flex md:overflow-x-auto md:scrolling-touch md:gap-5 md:pb-4 md:hide-scrollbar flex flex-col md:flex-row gap-3">
            {categories.map(category => {
              const isSelected = searchParams.get('category') === category.id;
              return (
                <Link 
                  key={category.id}
                  to={isSelected ? '/' : `/?category=${category.id}`}
                  className={`category-card group relative overflow-hidden rounded-lg transition-all duration-500 ease-in-out md:flex-shrink-0 md:min-w-[190px] ${
                    isSelected 
                      ? 'bg-[#22c55e]/5 border border-[#22c55e]/20 backdrop-blur-sm' 
                      : 'bg-black/10 hover:bg-black/20 border border-white/5 backdrop-blur-sm hover:border-[#22c55e]/15'
                  }`}
                >
                  <div className="relative p-4 flex items-center w-full">
                    <div className="flex items-center gap-3">
                      <div className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
                        isSelected ? 'text-[#22c55e]' : 'text-[#22c55e]/80'
                      }`}>
                        {renderIcon(category.icon, `${
                          category.id === 'reactions' ? 'w-6 h-6' : 'w-5 h-5'
                        } transition-transform duration-300 ${
                          isSelected ? 'transform scale-110' : 'group-hover:scale-105'
                        }`)}
                      </div>
                      <h3 className={`text-base font-medium transition-all duration-300 ease-in-out ${
                        isSelected 
                          ? 'text-[#22c55e]' 
                          : 'text-gray-400 group-hover:text-gray-200'
                      }`}>
                        {category.name}
                      </h3>
                    </div>
                  </div>
                  <div className={`absolute inset-0 bg-gradient-to-r from-transparent ${
                    isSelected ? 'to-[#22c55e]/5' : 'to-transparent'
                  } opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out`}></div>
                </Link>
              );
            })}
          </div>
          
          {/* Search Bar */}
          <div className="mt-6 relative">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                className="w-full bg-black/20 border border-white/5 focus:border-[#22c55e]/20 rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#22c55e]/20 transition-all duration-200"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2">
                <svg 
                  className="w-5 h-5 text-gray-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                  />
                </svg>
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <svg 
                    className="w-5 h-5" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" 
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div id="products" className="max-w-7xl mx-auto px-6 py-16">
        <section>
          <h2 className="text-2xl font-bold mb-6">{getCategoryTitle()}</h2>
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {filteredProducts.map(product => (
                <div key={product.id} className="transform-gpu">
                  <ProductCard 
                    {...product} 
                    image={product.image || DEFAULT_IMAGE}
                    deliveryType={product.deliveryType || 'serials'}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-gray-400">
                {searchTerm 
                  ? "No products found matching your search" 
                  : "No products available in this category"}
              </h3>
              <p className="text-gray-500 mt-2">
                {searchTerm 
                  ? "Try different keywords or browse all products" 
                  : "Please check back later or select another category"}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-[#22c55e] hover:text-[#15803d] transition-colors duration-200"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Catalog; 